import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Lock, Eye, EyeOff, CheckCircle, ArrowLeft, ArrowRight, Shield } from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import Config from '../services/config.json';

const ForgetPassword = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    mobileNo1: '',
    otp: '',
    password: '',
    cPassword: '',
    realOtp: 0,
    clientId: null
  });
  const [loading, setLoading] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showCPassword, setShowCPassword] = useState(false);

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      const response = await MainService.GetCompanyProfile();
      setCompanyProfile(response);
    } catch (error) {
      console.error('Error fetching company profile:', error);
    }
  };

  const checkMobileNo = async () => {
    if (!formData.mobileNo1) {
      showError('Enter Mobile No');
      return;
    }
    if (formData.mobileNo1.length !== 10 && formData.mobileNo1.length !== 9) {
      showError('Enter Valid Mobile No');
      return;
    }

    try {
      setLoading(true);
      const response = await MainService.CheckMobileNoTouble(formData.mobileNo1);
      
      if (response.Status === "Success") {
        showSuccess('OTP Send To Your Mobile');
        setFormData(prev => ({
          ...prev,
          realOtp: response.OTP,
          clientId: response.ClientId
        }));
        setCurrentStep(2);
      } else {
        showError('Mobile No Not Registered');
      }
    } catch (error) {
      console.error('Error checking mobile number:', error);
      showError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const checkOTP = () => {
    if (!formData.otp) {
      showError('Enter OTP');
      return;
    }
    if (formData.otp !== formData.realOtp.toString()) {
      showError('Enter Valid OTP');
      return;
    }
    setCurrentStep(3);
  };

  const recoverPassword = async () => {
    if (!formData.password) {
      showError('Enter Your Password');
      return;
    }
    if (!formData.cPassword) {
      showError('Confirm Password');
      return;
    }
    if (formData.password !== formData.cPassword) {
      showError('Password Not Matching');
      return;
    }

    try {
      setLoading(true);
      const recoverData = {
        ...formData,
        Id: formData.clientId
      };
      
      const response = await MainService.RecoverClient(recoverData);
      
      if (response) {
        showSuccess('Successfully Recovered');
        navigate('/login');
      } else {
        showError('Network Error');
      }
    } catch (error) {
      console.error('Error recovering password:', error);
      showError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Mobile Verification', icon: Phone },
    { number: 2, title: 'OTP Verification', icon: CheckCircle },
    { number: 3, title: 'Reset Password', icon: Lock }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left Side - Welcome */}
          <div className="hidden lg:block">
            <div className="bg-gradient-to-br from-blue-600 to-green-600 rounded-3xl p-12 text-white text-center">
              <div className="mb-8">
                <img src="/images/dboy.png" alt="Delivery" className="w-32 h-32 mx-auto mb-6" />
                <h1 className="text-4xl font-bold mb-4">Hello, Friend!</h1>
                <p className="text-xl mb-2">If you are an existing user</p>
                <h3 className="text-2xl font-semibold mb-8">Login to your account now!</h3>
                <button
                  onClick={() => navigate('/login')}
                  className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-xl hover:bg-gray-100 transition-colors duration-200"
                >
                  Login Now
                </button>
              </div>
              <div className="mt-8 text-sm opacity-90">
                <p>Client Portal | Powered By{' '}
                  <a 
                    href="https://ainsoftsolutions.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-200"
                  >
                    Ainsoft Solutions
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {/* Company Logo and Title */}
            <div className="text-center mb-8">
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Welcome To {companyProfile?.Name || 'Client Portal'}
              </h4>
            </div>

            {/* Progress Steps */}
            <div className="mb-8">
              <div className="flex items-center justify-center">
                <div className="flex items-center space-x-4">
                  {[0, 1, 2].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        currentStep >= step 
                          ? 'bg-primary-600 text-white' 
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {currentStep > step ? <CheckCircle className="w-4 h-4" /> : step + 1}
                      </div>
                      {step < 2 && (
                        <div className={`w-12 h-1 mx-2 ${
                          currentStep > step ? 'bg-primary-600' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Form Title */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary-600 mr-2" />
                Recover Account Now
              </h3>
            </div>

            {/* Step 0: Mobile Number */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Enter Your Mobile Number</h3>
                  <p className="text-gray-600">We'll send you an OTP to verify your number</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-primary-600" />
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    value={formData.mobileNo1}
                    onChange={(e) => setFormData(prev => ({ ...prev, mobileNo1: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                    placeholder="Enter your mobile number"
                    maxLength="10"
                  />
                </div>

                <button
                  onClick={checkMobileNo}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <span>Continue</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Step 1: OTP Verification */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Verify OTP</h3>
                  <p className="text-gray-600">Enter the OTP sent to {formData.mobileNo1}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                    <Lock className="w-4 h-4 mr-2 text-primary-600" />
                    OTP Code
                  </label>
                  <input
                    type="text"
                    value={formData.otp}
                    onChange={(e) => setFormData(prev => ({ ...prev, otp: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 text-center text-lg tracking-widest"
                    placeholder="Enter OTP"
                    maxLength="6"
                  />
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <button
                    onClick={checkOTP}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <span>Verify</span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Password Reset */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Create New Password</h3>
                  <p className="text-gray-600">Choose a strong password for your account</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                      <Lock className="w-4 h-4 mr-2 text-primary-600" />
                      New Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                      <Lock className="w-4 h-4 mr-2 text-primary-600" />
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showCPassword ? 'text' : 'password'}
                        value={formData.cPassword}
                        onChange={(e) => setFormData(prev => ({ ...prev, cPassword: e.target.value }))}
                        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCPassword(!showCPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showCPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <button
                    onClick={recoverPassword}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Shield className="w-4 h-4" />
                        <span>Recover Now</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Back to Login */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600">
                Remember your password?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-primary-600 hover:text-primary-700 font-semibold underline"
                >
                  Login here
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;
